// Bates College: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Bates's Common Data Set 2025-2026 (CDS_2025-2026.pdf), linked from the
//    Institutional Research, Analysis & Planning Common Data Set page:
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table (3 applicants of unknown residency sit in the total only), C8
//    test policy for Fall 2027, C21 early decision counts, C22 (no early
//    action), G1/G5 2026-2027 costs (not marked provisional).
//    Quirk: C21's early decision counts sit under the form's line "For the
//    Fall 2024 entering class", but they differ from the 2024-2025 edition's
//    own counts (1,128 applied, 306 admitted), so they are read here as the
//    Fall 2025 class the rest of this edition reports. The CDS gives one
//    count for Early Decision I and II together.
//  - Bates Admission: Application Rounds and Timeline (deadlines, decision
//    dates, the binding terms), Optional and Required Testing, Apply (checked
//    for any next-cycle change) and FAQs (arts portfolios).
//    The pages carry no year; they are the pages as posted for applications
//    due this fall and winter, for Fall 2027 entry.
//  - College Scorecard, school id 160977 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Bates Common Data Set 2025-2026',
        url: 'https://www.bates.edu/research/common-data-set/',
        read: '2026-10-01',
    },
    rounds: {
        label: 'Bates Admission, Application Rounds and Timeline',
        url: 'https://www.bates.edu/admission/apply/application-options/',
        read: '2026-10-01',
    },
    testing: {
        label: 'Bates Admission, Optional and Required Testing',
        url: 'https://www.bates.edu/admission/apply/optional-testing/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Bates Admission, Apply',
        url: 'https://www.bates.edu/admission/apply/apply/',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Bates Admission, Frequently Asked Questions',
        url: 'https://www.bates.edu/admission/apply/frequently-asked-questions/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?160977-Bates-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'bates-college',
    kbId: 'bates_college',
    name: 'Bates College',
    shortName: 'Bates',
    location: 'Lewiston, ME',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Bates brand identity guide); decoration only.
    branding: {
        primary: '#881124',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/bates_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 9660,
        admitted: 1433,
        enrolled: 493,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 356, admitted: 95 },
            { group: 'Out-of-state', applied: 4262, admitted: 1178 },
            { group: 'International', applied: 5039, admitted: 160 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (3 applicants of unknown residency are in the total only)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Bates admits students to the college, not to a major, and its Common Data Set reports one first-year acceptance rate.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early Decision',
        note: 'The Common Data Set gives one count for Early Decision I and II together. Bates has no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II', applied: 949, admitted: 318 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Bates, not to a major. One thing is optional:',
        rows: [
            { school: 'Arts portfolio', requirement: 'Optional, for any applicant, arts major or not, who wants to show their interest in the creative community on campus. You upload it in the Bates application portal after you apply; it is due November 20 for Early Decision I and January 15 for Early Decision II and Regular Decision.' },
        ],
        source: 'faqs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 10' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'By December 20', binding: true },
            { plan: 'Early Decision II', deadline: 'January 10', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 10', decision: 'By April 1', binding: false },
        ],
        note: 'Bates takes the Common Application and the Coalition Application, Powered by Scoir. QuestBridge Match applicants apply by October 1. Admitted students reply by January 8 (Early Decision I), February 19 (Early Decision II) or May 1 (Regular Decision). Bates has no mid-year or rolling admission.',
        source: 'rounds',
        binding: 'Early Decision is binding: if admitted, you withdraw every application to other colleges, and you may not apply to another binding Early Decision program. You can switch a Regular Decision or Early Decision II application to Early Decision I by filing the Early Decision agreement by December 1, or a Regular Decision application to Early Decision II by February 1.',
        bindingSource: 'rounds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'The SAT and ACT are optional for everyone applying to Bates, as they have been since 1984. If you want your scores considered, Bates superscores both tests and accepts official scores or unofficial scores you enter on your application; official scores are then required if you enroll.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 10.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73760 },
            { item: 'Required fees (first year)', amount: 475 },
            { item: 'Food and housing (on campus)', amount: 20800 },
            { item: 'Books and supplies', amount: 900 },
            { item: 'Transportation', amount: 250 },
            { item: 'Other expenses', amount: 1000 },
        ],
        total: 97185,
        note: 'For a first-year student living on campus. The $475 required fee is charged in the first year only; the Common Data Set lists $0 for other undergraduates.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 27390,
            byIncome: [
                { income: '$0 to $30,000', amount: 1899 },
                { income: '$30,001 to $48,000', amount: 7617 },
                { income: '$48,001 to $75,000', amount: 9606 },
                { income: '$75,001 to $110,000', amount: 21363 },
                { income: 'Over $110,000', amount: 46685 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Bates\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
